/**
 * WebSocket wire protocol — types only, no behaviour.
 *
 * `spec/asyncapi.yaml` is authoritative for the frame envelope, channel names,
 * cadences and limits (docs/06 §F1). There is no mature AsyncAPI 3.0 TypeScript
 * generator worth depending on, so these are hand-authored — and
 * `scripts/check-ws-types.mjs` fails the build if they drift from the spec in
 * either direction.
 *
 * Phase 2 adds connection.ts, subscriptions.ts, sequencer.ts and dedupe.ts on
 * top of this. Nothing here executes.
 */

// ---------------------------------------------------------------------------
// Frame envelope
// ---------------------------------------------------------------------------

/** Closed enum. Mirrors `components.schemas.FrameType`. */
export type FrameType =
  // client -> server
  | 'subscribe'
  | 'unsubscribe'
  | 'auth'
  | 'ping'
  // server -> client
  | 'ack'
  | 'pong'
  | 'error'
  | 'heartbeat'
  | 'snapshot'
  | 'delta'
  | 'update'
  | 'event'

/**
 * Mirrors `components.schemas.WsErrorCode`. Shares the REST error vocabulary so
 * the client has one error renderer and one retry rule across both transports.
 *
 * This list is exhaustive. Codes from the earlier protocol draft — the ones
 * naming a control-frame method or a subscription id, and the one naming a
 * buffer condition — do not exist: no such arguments exist to be wrong about,
 * and buffer overflow arrives as `SLOW_CONSUMER`. See docs/03 Appendix C.
 */
export type WsErrorCode =
  | 'UNKNOWN_CHANNEL'
  | 'INVALID_FRAME'
  | 'ALREADY_SUBSCRIBED'
  | 'NOT_SUBSCRIBED'
  | 'UNAUTHENTICATED'
  | 'SESSION_EXPIRED'
  | 'AUTH_ADDRESS_MISMATCH'
  | 'GEO_RESTRICTED'
  | 'MARKET_NOT_FOUND'
  | 'SUBSCRIPTION_LIMIT'
  | 'RATE_LIMITED'
  | 'SLOW_CONSUMER'
  | 'SEQUENCE_RESET'
  | 'SERVER_SHUTDOWN'
  | 'PAYLOAD_TOO_LARGE'
  | 'CONNECTION_LIMIT'
  | 'INTERNAL'

/** Codes that mean the named subscription is gone: re-snapshot, then resubscribe. */
export const TERMINAL_FOR_SUBSCRIPTION: ReadonlySet<WsErrorCode> = new Set<WsErrorCode>([
  'SESSION_EXPIRED',
  'GEO_RESTRICTED',
  'SLOW_CONSUMER',
  'SEQUENCE_RESET',
  'SERVER_SHUTDOWN',
  'INTERNAL',
])

// ---------------------------------------------------------------------------
// Channel names
// ---------------------------------------------------------------------------

export type Asset = 'btc' | 'eth'
export type Symbol_ = 'BTC-USD' | 'ETH-USD'
export type SeriesId = `${Asset}-updown-1m`
export type MarketId = `${SeriesId}-${number}`
export type CandleInterval = '1s' | '5s'

export type PublicChannel =
  | 'heartbeat'
  | `index.${Symbol_}`
  | `series.${SeriesId}`
  | `market.${MarketId}`
  | `book.${MarketId}`
  | `trades.${MarketId}`
  | `ticker.${MarketId}`
  | `candles.${MarketId}.${CandleInterval}`
  | `chat.${Asset}`

export type PrivateChannel =
  | 'orders'
  | 'fills'
  | 'positions'
  | 'balance'
  | 'settlements'
  | 'notifications'

export type ChannelName = PublicChannel | PrivateChannel

/**
 * `ticker` is the one sequenced-looking channel with **no `seq`** — every frame
 * is a complete replacement, so a dropped one costs a single stale tick and is
 * corrected 100ms later. The sequencer must exempt it explicitly rather than
 * treating a missing `seq` as a gap.
 */
export const UNSEQUENCED_CHANNEL_PREFIX = 'ticker.' as const

// ---------------------------------------------------------------------------
// Frames
// ---------------------------------------------------------------------------

interface FrameBase {
  type: FrameType
  channel?: ChannelName
  /** Server-assigned. Stable for the life of a subscription; not across reconnect. */
  sub_id?: number
  /** Monotonic per subscription, from 1. Absent on control frames and on `ticker`. */
  seq?: number
  /** Client correlation id, echoed verbatim on the matching ack/error/pong. */
  id?: string
}

export interface ClientFrame<D = unknown> extends FrameBase {
  client_time_ms?: number
  data?: D
}

export interface ServerFrame<D = unknown> extends FrameBase {
  /** Required on every server frame, which is what makes clock re-sync free (D9). */
  server_time_ms: number
  data?: D
}

// --- control payloads -------------------------------------------------------

export interface SubscribeData {
  /** Max 50 per frame; 200 subscriptions per connection. */
  channels: ChannelName[]
  /** `false` when the REST snapshot is already in flight — the cold-load default for `book`. */
  snapshot?: boolean
  /** Book ladder depth per side. Default 50, max 200. */
  depth?: number
}

export interface UnsubscribeData {
  /** Names channels, never `sub_id`s. */
  channels: ChannelName[]
}

export interface AuthData {
  token: string
}

export interface SubscribedEntry {
  channel: ChannelName
  sub_id: number
  /** The `seq` the first frame will carry — always 1. The gap detector anchors here. */
  seq: number
  snapshot_pending?: boolean
}

export interface FailedEntry {
  channel: string
  code: WsErrorCode
  detail: string
}

/** Partial failure lives inside a successful `ack`, never in an `error` frame. */
export interface SubscribeAckData {
  subscribed: SubscribedEntry[]
  failed: FailedEntry[]
  subscription_count?: number
}

export interface UnsubscribeAckData {
  unsubscribed: ChannelName[]
  failed: FailedEntry[]
  subscription_count?: number
}

export interface PongData {
  client_time_ms: number
  recv_time_ms?: number
  rtt_hint_ms?: number
}

export interface HeartbeatData {
  connection_id: string
  subscriptions: number
  authenticated: boolean
  interval_ms: number
  /** Compare against the held revision; any difference means re-fetch `/config`. */
  config_revision: number
  mode: 'NORMAL' | 'POST_ONLY' | 'CANCEL_ONLY' | 'HALTED' | 'MAINTENANCE'
  venue_status?: 'OK' | 'DEGRADED' | 'HALTED' | 'MAINTENANCE'
  address?: string
  session_expires_at_ms?: number
  server_build?: string
}

/** RFC 9457 problem object, same vocabulary as a REST error body. */
export interface WsProblemData {
  type: string
  title: string
  status: number
  code: WsErrorCode
  detail: string
  retryable: boolean
  channel?: string
  /** `true` means the subscription is gone — re-snapshot before resubscribing. */
  terminal?: boolean
  unsubscribed?: ChannelName[]
  retry_after_ms?: number
  meta?: Record<string, unknown>
}

// --- discriminated server frames -------------------------------------------

export type ServerControlFrame =
  | (ServerFrame<SubscribeAckData | UnsubscribeAckData> & { type: 'ack' })
  | (ServerFrame<PongData> & { type: 'pong' })
  | (ServerFrame<WsProblemData> & { type: 'error' })
  | (ServerFrame<HeartbeatData> & { type: 'heartbeat'; channel: 'heartbeat' })

export type ServerDataFrame = ServerFrame & {
  type: 'snapshot' | 'delta' | 'update' | 'event'
  channel: ChannelName
}

export type AnyServerFrame = ServerControlFrame | ServerDataFrame

/**
 * Frames routed by `channel`.
 *
 * The four data frame types, plus `heartbeat` — which is a distinct `type` *and*
 * a channel of its own. A client drives its liveness and clock logic off
 * `type == "heartbeat"` without parsing any channel payload, and it is also
 * sequenced: a gap in heartbeat `seq` proves frames were dropped even on an idle
 * socket, which is the only evidence available when nothing is subscribed.
 */
export type ChannelFrame =
  | ServerDataFrame
  | (ServerFrame<HeartbeatData> & { type: 'heartbeat'; channel: 'heartbeat' })

// ---------------------------------------------------------------------------
// Limits — the values not carried by `config.ws`
// ---------------------------------------------------------------------------

export const WS_LIMITS = {
  maxChannelsPerFrame: 50,
  maxSubscriptionsPerConnection: 200,
  /** Two missed pongs is a dead connection; reconnect rather than waiting for TCP. */
  missedPongsBeforeDead: 2,
} as const
